import { Container, Icon, Text, buttonClasses } from '@nexa/ui';
import wordmark from '@/assets/newera-wordmark.svg';
import { hostnameOf, ORGANIZATION, SITE } from '@/core/config/site.ts';
import { useCopy } from '@/core/i18n';
import { PageLink } from './PageLink.tsx';
import { useHomeAnchor } from './homeAnchor.ts';

/**
 * `footer` — `template.html:1054`. Thay bản tối giản 38 dòng của lô 0.
 *
 * Logo là WORDMARK (ảnh chữ "NewEra", 906×183) chứ KHÔNG phải `NewEraMark` — con dấu vuông
 * 209×183 mà `Header` dùng. Hai asset khác nhau trong chính bản thiết kế; thiết kế footer dùng
 * `<img>` cao 26px, nên giữ `<img>` thay vì inline SVG.
 *
 * KHÔNG import gì từ `@/features/*` — `contract.md` §1.4 cấm `core/` phụ thuộc `features/`, và
 * cổng eslint canh luật đó đã được vá nên nó THẬT. Liên kết tới section là chuỗi `href="#…"`,
 * không phải import, nên footer vẫn trỏ được vào 7 anchor mà không vi phạm ranh giới tầng.
 *
 * Ba trong bảy anchor (`#security` `#pricing` `#faq`) do chính lô D sinh ra. Trước lô D, dựng
 * footer đúng thiết kế sẽ cho 3 LINK CHẾT — đó là lý do file này land SAU khi bốn section đã vào
 * `HomePage`, không phải trước.
 *
 * Nhãn cột dùng `--tracking-footer-label` (.12em) — đúng số đo của thiết kế. Nợ D-2 của lô D đã
 * trả ở vé #10: lúc đó token .12em chưa tồn tại và `tokens.css` nằm ngoài phạm vi lô D, nên footer
 * phải mượn bậc `wide` (.1em) và chịu lệch ~1.9px trên nhãn dài nhất.
 *
 * Token đặt tên theo VAI, không theo độ, và đó là quyết định của user (2026-08-21) có lý do: bậc
 * `wide` cũng đặt tên theo độ rồi bị footer mượn chéo — chú thích của nó ghi "nhãn cột trong mock".
 * Đặt tên theo độ lần nữa là lặp đúng cái bẫy đã sinh ra D-2. Thêm vào đó, `widest` là .62em nên
 * bộ wide/widest vốn KHÔNG phải một thang liên tục; nhét một bậc vào giữa làm nó trông như thang.
 *
 * Cột "Liên hệ" là `<span>`, KHÔNG phải `<a>` — đúng thiết kế `:1062`.
 *
 * Từ 2026-08-27 có CỘT THỨ TƯ trong lưới copy — "Pháp lý", 5 link trang nội bộ — nên lưới `xl` là
 * 5 cột chứ không phải 4. Đây là ngoại lệ user chốt, KHÔNG phải chép sai thiết kế; lý lẽ đầy đủ
 * (vì sao cần, vì sao đúng 5 trang đó, vì sao không lấy tiêu đề văn bản làm nhãn) khai TẠI CHỖ ở
 * `packages/content/src/copy/<locale>/footer.ts`, không nhân bản ở đây.
 *
 * Từ 2026-09-28 có CỘT THỨ NĂM — "Ứng dụng" (macOS · Windows, cùng trỏ `#download`), nên lưới `xl`
 * là 6 cột. Cũng là ngoại lệ user chốt; bốn số mồi `.nx-cv-footer` đã đo lại cùng lượt.
 *
 * 🔴 Hệ quả KHÔNG tự lộ: thêm một cột làm footer CAO HƠN ở base (1 cột: 5 khối thay vì 4) và ở
 * `md`/`lg` (2 cột: 3 hàng thay vì 2). Bốn số mồi `--nx-cv-h` của `.nx-cv-footer` trong
 * `packages/ui/src/motion.css` vì thế phải ĐO LẠI — mồi thấp hơn thật là thanh cuộn nhảy lúc footer
 * vào viewport, và `pnpm verify` KHÔNG bắt được (cổng `check:cv` cần bản build + chromium).
 *
 * KHÔNG có `LanguageSwitcher` ở đây, và đó là GỠ CÓ CHỦ Ý (user chốt 2026-08-20), không phải bỏ
 * sót: thiết kế `:1054-1068` cho cột 1 đúng logo + tagline, và thanh đáy đúng HAI `<span>` (bản
 * quyền, ghi chú tên miền). Bản 38 dòng của lô 0 có toggle ở đây; nó là thứ thừa hưởng, không phải
 * quyết định thiết kế.
 *
 * Đường đổi ngôn ngữ KHÔNG mất: `Header.tsx:174` render `LanguageSwitcher` trong một `div` không
 * có bậc ẩn theo khổ, tức nó hiện ở MỌI khổ kể cả mobile. Đã kiểm trước khi gỡ — gỡ mà không kiểm
 * chỗ còn lại là cắt mất lối đổi ngôn ngữ trên điện thoại.
 *
 * NHỊP DỌC nằm trên chính `<footer>` (`padding:56px … 40px`, `:1054`), KHÔNG trên `Container` của
 * grid. Đặt `pb-10` lên `Container` thì 40px đó rơi vào GIỮA grid và dải đáy (thành 40+36=76) còn
 * chân footer còn 0 ⇒ dòng bản quyền dính sát mép dưới trang và kẻ ngang tụt 40px so với thiết kế.
 *
 * Bẫy của chỗ này, và lý do nó không tự lộ: TỔNG chiều cao trùng khít thiết kế —
 * `40+36+20+0 = 96 = 36+20+40`. Nên mọi phép đo CHIỀU CAO footer, kể cả `design:diff` ở 1440, đều
 * cho 0 lệch; chỉ vị trí kẻ ngang và khoảng chân mới lộ. Cổng không bắt được vì nó đo đúng thứ nó
 * đo, và thứ đó không phải thứ hỏng.
 *
 * T1 lazy (design doc §8.4) trên footer: file này KHÔNG đi qua `Section` (nó có nhịp dọc riêng,
 * xem trên), nên không ai gắn hộ nó class bật containment. Phải khai TAY cả hai class dưới đây —
 * một để bật `content-visibility`, một để mang số mồi. Thiếu class bật thì `contain-intrinsic-size`
 * không bao giờ có hiệu lực và số mồi không có ai đọc: mất T1 mà không có gì đỏ. Thiếu class mồi
 * thì footer rơi về lưới an toàn 600 (thấp hơn thật 250 ở bậc hẹp nhất) ⇒ thanh cuộn nhảy.
 * Cổng canh cả hai chiều: `apps/web/script/check-cv-primes.mjs` — KHÔNG nằm trong `pnpm verify`,
 * vì nó cần một bản build cộng chromium.
 */
/*
 * Class của link trong cột footer, tách thành HẰNG vì từ lô cột Pháp lý có HAI nhánh render
 * (`PageLink` cho trang nội bộ · `<a>` cho anchor và link ngoài) và chúng phải trông GIỐNG NHAU.
 * Nhân bản chuỗi 6 utility ở hai chỗ là chỗ để hai nhánh trôi khỏi nhau trong im lặng — không cổng
 * nào so hai className với nhau.
 */
const COLUMN_LINK_CLASS =
  'text-body-sm text-[var(--on-dark-text-80)] transition [transition-duration:var(--duration-hover)] hover:text-white';

export function Footer() {
  const copy = useCopy();
  const homeAnchor = useHomeAnchor();
  const year = new Date().getFullYear();

  return (
    <footer className="nx-cv nx-cv-footer bg-surface-navy pt-14 pb-10 text-white">
      <Container className="grid gap-10 md:grid-cols-2 xl:grid-cols-[1.4fr_1fr_1fr_1fr_1fr_1fr]">
        <div>
          <img src={wordmark} alt={copy.footer.logoAlt} className="block h-[26px] w-auto" />
          <Text size="body-sm" tone="on-dark-soft" className="mt-4 max-w-[34ch]">
            {copy.footer.tagline}
          </Text>

          {/*
            Nút mailto THẲNG, không mở dialog: dialog tồn tại để trả lời một nút CTA chưa có đích
            thật ("Dùng thử miễn phí" / "Đặt lịch demo"). Ở đây người dùng đang đi tìm cách liên
            hệ, nên chèn thêm một lớp hỏi lại là bắt họ bấm hai lần cho cùng một việc.

            Đặt ở cột ĐẦU chứ không ở cột "Liên hệ": cột đó là cột phải cùng của lưới 4 cột, nên
            nút treo ở mép phải trang và trông như bị đẩy ra ngoài — user chỉ đúng chỗ đó. Ở cột
            đầu nó nằm dưới tagline, cân với khối thương hiệu, và vẫn là hành động rõ ràng nhất
            của footer. `on-dark` vì nền navy.
          */}
          <a
            href={`mailto:${copy.contact.email}`}
            className={`${buttonClasses('on-dark', 'md')} mt-6 gap-2`}
          >
            <Icon name="messages-square" size={17} />
            {copy.contact.footerCta}
          </a>
        </div>

        {copy.footer.columns.map((column) => (
          <div key={column.label}>
            <p className="text-caption font-bold tracking-footer-label text-[var(--on-dark-text-50)] uppercase">
              {column.label}
            </p>
            <ul className="mt-[14px] flex flex-col gap-[9px]">
              {column.links.map((link) =>
                'page' in link ? (
                  // Trang NỘI BỘ đi qua `PageLink`, không qua `<a href>`: nó giải đường dẫn bằng
                  // `pathFor(page, locale)` nên slug đổi ở bảng route là footer đi theo, và prefix
                  // locale không thể quên. Xem docblock `FooterLink` ở `copy/<locale>/footer.ts`.
                  <li key={link.page}>
                    <PageLink page={link.page} className={COLUMN_LINK_CLASS}>
                      {link.label}
                    </PageLink>
                  </li>
                ) : (
                  // Khoá theo NHÃN, không theo `href`: cột "Ứng dụng" có hai link cùng trỏ `#download`.
                  <li key={link.label}>
                    <a
                      // CHỈ anchor mới đi qua `homeAnchor`. Cột footer có cả link NGOÀI
                      // (`https://newera.inc/…`), và bọc chúng cho ra `/vi/#https://…` — bản đầu
                      // của bản vá này làm đúng thế, `footer-anchors.test.tsx` bắt được.
                      href={link.href.startsWith('#') ? homeAnchor(link.href) : link.href}
                      className={COLUMN_LINK_CLASS}
                    >
                      {link.label}
                    </a>
                  </li>
                ),
              )}
            </ul>
          </div>
        ))}

        <div>
          <p className="text-caption font-bold tracking-footer-label text-[var(--on-dark-text-50)] uppercase">
            {copy.footer.contact.label}
          </p>
          <ul className="mt-[14px] flex flex-col gap-[9px]">
            {copy.footer.contact.items.map((item) => (
              <li key={item} className="text-body-sm text-[var(--on-dark-text-80)]">
                {item}
              </li>
            ))}
          </ul>
        </div>
      </Container>

      {/*
        `border-t` phải nằm trên MỘT DIV CON, KHÔNG phải trên `Container`.

        `Container` render đúng một div mang `px-gutter` cộng `max-w-[calc(1240px + 2*gutter)]` —
        tức bề rộng BORDER BOX của nó là 1240 + 2 gutter, để CONTENT box còn đúng 1240. Đặt
        `border-t` lên chính nó thì kẻ ngang trải hết border box và **thò ra một gutter mỗi bên**,
        rộng hơn chính lưới 4 cột phía trên. Đo được ở 1440: lưới content 1240 bắt đầu tại x=100,
        còn kẻ ngang 1304 bắt đầu tại x=68.

        Thiết kế không gặp chuyện này vì nó đặt padding ngang ở `<footer>` (`padding:56px 32px 40px`)
        nên div trong là `max-width:1240px` KHÔNG padding — kẻ ngang trùng khít cột nội dung.

        Lỗi này THỪA HƯỞNG từ bản footer của lô 0 (`v2:Footer.tsx:31` cũng truyền `border-t` qua
        `className` của `Container`), không phải do lô D nghĩ ra — nhưng lô D mang nó theo khi dựng
        lại. Hai lượt truyền `border-*` qua `className` của `Container` trong toàn repo đều ở file này.
      */}
      <Container className="mt-9">
        <div className="flex flex-wrap justify-between gap-4 border-t border-[var(--on-dark-line-14)] pt-5 text-caption text-[var(--on-dark-text-50)]">
          <span>
            {/*
              KHÔNG có dấu chấm cứng sau `legalName`: giá trị hiện tại là `NewEra Inc.` — dấu chấm
              thuộc về chữ viết tắt "Inc.", nó đã nằm TRONG chuỗi. Nối thêm `.` ở đây cho
              `© 2026 NewEra Inc.. Bảo lưu mọi quyền.` (render thật ở cả hai locale trước khi vá).
              Thiết kế `:1065` có ĐÚNG MỘT dấu chấm.
            */}
            © {year} {ORGANIZATION.legalName} {copy.footer.rights}
          </span>
          <span>{hostnameOf(SITE.url)}</span>
        </div>
      </Container>
    </footer>
  );
}
